import { isUndefined, takeRight } from 'lodash';
import { Layer as ParseLayer, Psd, readPsd } from '@meitu-graphic/ag-psd';
import { removeEmptyChildren, traverseTree, traverseTrees } from '@meitu/saas-editor-core-next/es/utils/tree-kit';
import { getDefaultClientBackgroundValue } from '@meitu/saas-editor-core-next/es/version-parse/constants';
import {
    DEFAULT_FONT_FAMILY,
    LayerType,
    type IFont,
    type ILayerProps,
} from '@meitu/saas-editor-core-next/es/version-types';
import { createFileParserEditor } from '../../shared-editor';
import { getBgLayer } from './parse-bg-layer';
import { getGroupLayer, recalculateGroupSize } from './parse-group';
import { getImageLayer } from './parse-image-layer';
import { fixFont, getTextLayer } from './parse-text-layer';
import { getVectorLayer } from './parse-vector-layer';
import {
    IParsePsdToClientTemplateResult,
    IStage,
    PsdParseErrorType,
    PsdParseWarnType,
    UploadImage,
    WarnOrErrorCallbackFunc,
} from './types';
import {
    getUrlByCanvas,
    isGroupLayer,
    isImageLayer,
    isTextLayer,
    isVectorLayer,
    isVertical,
    setCutMaskLayers,
    sliceCanvas,
    traverseTreePostOrder,
} from './utils';

const noFixFontLayer: string[] = [];

/**
 * @description 格式化文件名 文件名不携带.psd
 * @param fileName
 */
export const formatFileName = (fileName: string): string => {
    return (fileName = fileName.replace(/.psd/g, ''));
};

/**
 * @description 清除隐藏图层 会修改到原数据
 * @param layers 所有图层数据
 *
 */
export const removeHiddenLayers = (layers: ParseLayer[]) => {
    let index = layers.length - 1;
    while (index >= 0) {
        const thisIndex = index;
        const currentLayer = layers[thisIndex];
        const { clipping, hidden, children } = currentLayer;
        // 剪切蒙版效果 => 蒙版隐藏时
        // 需要清除图片层 、 蒙版层
        if (clipping && layers[thisIndex - 1]?.hidden) {
            layers.splice(thisIndex - 1, 2);
            index -= 1;
        } else if (hidden) {
            // 清除隐藏图层
            layers.splice(thisIndex, 1);
        } else {
            // 存在分组继续清除
            if (children && children.length) {
                removeHiddenLayers(children);
            }
        }
        index -= 1;
    }
};

const getClientLayer = async (
    stageWidth: number,
    stageHeight: number,
    clientLayers: ILayerProps[],
    parentLayer: ParseLayer,
    layer: ParseLayer,
    fontList: any[],
    uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    noReplaceFontFamily = false,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps | null>,
) => {
    if (isGroupLayer(layer)) {
        const groupLayer = await getGroupLayer(
            stageWidth,
            stageHeight,
            clientLayers,
            parentLayer,
            layer,
            uploadFile,
            warnOrErrorCallback,
            parseNoLimit,
            parseLayerCallback,
        );
        return groupLayer;
    } else if (isTextLayer(layer)) {
        const textLayer = await getTextLayer(
            stageWidth,
            stageHeight,
            parentLayer,
            layer,
            fontList,
            noFixFontLayer,
            warnOrErrorCallback,
            noReplaceFontFamily,
            parseNoLimit,
            parseLayerCallback,
        );
        console.log('textLayer ==>6', textLayer?.width);
        return textLayer;
    } else if (isVectorLayer(layer)) {
        const vectorLayer = await getVectorLayer(
            stageWidth,
            stageHeight,
            parentLayer,
            layer,
            uploadFile,
            warnOrErrorCallback,
            parseNoLimit,
            parseLayerCallback,
        );
        return vectorLayer;
    } else if (isImageLayer(layer)) {
        const imageLayer = await getImageLayer(
            stageWidth,
            stageHeight,
            parentLayer,
            layer,
            uploadFile,
            warnOrErrorCallback,
            parseNoLimit,
            parseLayerCallback,
        );
        return imageLayer;
    }
};

export const parseLayerToClientLayer = async (
    stageWidth: number,
    stageHeight: number,
    layers: ParseLayer[],
    fontList: any[],
    uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    noParseBgLayer = false,
    noReplaceFontFamily = false,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps>,
) => {
    const clientLayers = [] as ILayerProps[];

    let ls = layers;
    if (!noParseBgLayer) {
        const bgLayer = await getBgLayer(
            stageWidth,
            stageHeight,
            layers[0],
            uploadFile,
            warnOrErrorCallback,
            parseNoLimit,
            parseLayerCallback,
        );

        if (bgLayer) {
            clientLayers.push(bgLayer);
            ls = takeRight(layers, layers.length - 1);
        } else {
            clientLayers.push(getDefaultClientBackgroundValue(stageWidth, stageHeight));
        }
    }

    for (let i = 0; i < ls.length; i++) {
        const parentLayer = ls[i];

        if (
            parentLayer.children &&
            parentLayer.children.length >= 1 &&
            (parentLayer.opacity !== 1 || parentLayer.fillOpacity !== 1)
        ) {
            traverseTree(parentLayer, (childLayer: ParseLayer) => {
                if (childLayer.id === parentLayer.id) {
                    return;
                }
                childLayer.opacity =
                    (parentLayer.opacity || 1) * (childLayer.opacity || 1) * (parentLayer.fillOpacity || 1);
            });
        }

        await traverseTreePostOrder(parentLayer, parentLayer, async (parentLayer: ParseLayer, layer: ParseLayer) => {
            const clientLayer = await getClientLayer(
                stageWidth,
                stageHeight,
                clientLayers,
                parentLayer,
                layer,
                fontList,
                uploadFile,
                warnOrErrorCallback,
                noReplaceFontFamily,
                parseNoLimit,
                parseLayerCallback,
            );
            if (clientLayer) {
                clientLayers.push(clientLayer);
            }
        });
    }

    return clientLayers;
};

/**
 *
 * @param psd psd json 源数据
 * @param uploadFn 上传图片回调函数
 */
export const previewToUrl = async (name: string, uploadFn: UploadImage, canvas?: HTMLCanvasElement) => {
    if (!canvas) {
        return '';
    }

    const url = await getUrlByCanvas(name || '', canvas, uploadFn);

    return url;
};

export const groupLayers = (layers: ILayerProps[]) => {
    const rootLayers = layers.filter((layer) => !layer.parentId);
    const layersMap = layers.reduce(
        (total, layer) => {
            total[layer.id] = layer;
            return total;
        },
        {} as { [key: string]: ILayerProps },
    );

    layers.forEach((layer) => {
        const parent = layersMap[layer.parentId || ''];

        parent?.children.push(layer);
    });
    return rootLayers;
};

export const parseLayers = async (
    stageWidth: number,
    stageHeight: number,
    layers: ParseLayer[],
    fontList: IFont[],
    uploadFile: UploadImage,
    noParseBgLayer = false,
    name: string,
    canvas?: HTMLCanvasElement,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    noReplaceFontFamily = false,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps>,
) => {
    warnOrErrorCallback?.('删除隐藏图层', PsdParseWarnType.Info);
    // 删除隐藏图层
    removeHiddenLayers(layers);

    warnOrErrorCallback?.('解析图层数据', PsdParseWarnType.Info);
    // 将图层数据转化为客户端数据
    let clientLayers = await parseLayerToClientLayer(
        stageWidth,
        stageHeight,
        layers,
        fontList,
        uploadFile,
        warnOrErrorCallback,
        noParseBgLayer,
        noReplaceFontFamily,
        parseNoLimit,
        parseLayerCallback,
    );

    await setCutMaskLayers(stageWidth, stageHeight, clientLayers, layers, uploadFile, warnOrErrorCallback);
    console.log('before fix font ===', JSON.stringify(clientLayers));
    warnOrErrorCallback?.('调整文本图层宽高， 耗时可能较长请耐心等待', PsdParseWarnType.Info);

    const replaceFonts = await fixFont(
        clientLayers,
        fontList,
        DEFAULT_FONT_FAMILY().name,
        noFixFontLayer,
        noReplaceFontFamily,
    );

    replaceFonts.forEach(({ layer, replaceFont }) => {
        warnOrErrorCallback?.(`文本图层${layer.title}：${replaceFont}字体被替换`, PsdParseWarnType.Warn, layer.id);
    });

    warnOrErrorCallback?.('计算图层组宽高', PsdParseWarnType.Info);
    await recalculateGroupSize(clientLayers);
    warnOrErrorCallback?.('生成图预览图', PsdParseWarnType.Info);
    // 获取预览图
    const preview = await previewToUrl(name, uploadFile, canvas);

    warnOrErrorCallback?.('合成图层组', PsdParseWarnType.Info);
    // 拼凑拍平的数据
    clientLayers = groupLayers(clientLayers);
    warnOrErrorCallback?.('解析结束', PsdParseWarnType.Info);

    clientLayers = removeEmptyChildren(
        clientLayers,
        (node) => (node as ILayerProps).layerType !== LayerType.GROUP_LAYER,
    ) as ILayerProps[];

    return {
        clientLayers,
        preview,
        stageWidth,
        stageHeight,
        replaceFonts,
        name: '',
        settingType: '',
    };
};

// 规范设计师 ppt 模板 psd 命名
// 旧值需要做一次映射
const settingTypeMap = {
    1001: 0,
    1002: 1000,
    2001: 1,
    3001: 2,
    4001: 3,
    5001: 4,
    6001: 5,
    8001: 99,
    8002: 100,
    9001: 9999,
} as any;

export function getOldSettingType(settingType: any) {
    const res = settingTypeMap[settingType];
    return isUndefined(res) ? settingType : res;
}

export const parsePsdToClientTemplate = async (
    psdFile: File,
    fileBuffer: ArrayBuffer,
    fileName: string,
    _uploadFile: UploadImage,
    warnOrErrorCallback?: WarnOrErrorCallbackFunc,
    noParseBgLayer = false,
    noReplaceFontFamily = false,
    parseNoLimit = false,
    parseLayerCallback?: (originLayer: ParseLayer, resultLayer: ILayerProps) => Promise<ILayerProps>,
): Promise<IParsePsdToClientTemplateResult | null> => {
    const errorTypeRef: { current: PsdParseErrorType | null } = { current: null };
    const uploadFile: UploadImage = async (...args) => {
        try {
            return await _uploadFile(...args);
        } catch (error) {
            console.log('uploadFile error ===>', error);
            errorTypeRef.current = window.navigator.onLine ? PsdParseErrorType.UploadApi : PsdParseErrorType.Network;
            throw error;
        }
    };
    try {
        const fileParserEditor = await createFileParserEditor();
        fileParserEditor.init();
        const fontList = fileParserEditor.services.font.getFonts();
        warnOrErrorCallback?.('读取psd数据', PsdParseWarnType.Info);
        const psd = Object.assign(readPsd(fileBuffer), { name: fileName });
        const resolution = Math.round(psd.imageResources?.resolutionInfo?.horizontalResolution || 72);
        // console.log('psd file====>',psd,resolution);

        if (!psd.children || psd.children.length === 0) {
            // console.log('psd.children===', JSON.stringify(psd));
            warnOrErrorCallback?.('读取psd数据为空文件', PsdParseWarnType.Error);
            return null;
        }

        console.log('psd.children===', JSON.stringify(psd.children));

        // 格式化文件名 文件名不携带.psd
        psd.name = formatFileName(psd.name);

        const stages = [] as IStage[];

        if (psd.artboards) {
            if (psd.children.length > 30 && !parseNoLimit) {
                warnOrErrorCallback?.(`仅支持画板拓展个数为30个，请减少画板数量重新上传`, PsdParseWarnType.Error);
                return null;
            }

            const rects = [] as {
                top: number;
                left: number;
                bottom: number;
                right: number;
            }[];
            for (let i = 0; i < psd.children.length; i++) {
                rects.push(
                    psd.children[i].artboard?.rect || {
                        left: 0,
                        top: 0,
                        right: 0,
                        bottom: 0,
                    },
                );
            }

            for (let i = 0; i < rects.length; i++) {
                for (let j = 0; j < rects.length; j++) {
                    if (
                        (rects[i].left - rects[j].left) * (rects[i].left - rects[j].right) < 0 ||
                        (rects[i].right - rects[j].left) * (rects[i].right - rects[j].right) < 0 ||
                        (rects[i].top - rects[j].top) * (rects[i].top - rects[j].bottom) < 0 ||
                        (rects[i].bottom - rects[j].top) * (rects[i].bottom - rects[j].bottom) < 0
                    ) {
                        warnOrErrorCallback?.(`不允许存在画板嵌套，请重新上传。`, PsdParseWarnType.Error);
                        return null;
                    }
                }
            }

            // 是否是竖版
            const vertical = isVertical(psd.children);

            const childrenCanvas = sliceCanvas(psd.canvas, psd.children, vertical);

            for (let i = 0; i < psd.children.length; i++) {
                const item = psd.children[i];
                warnOrErrorCallback?.(`正在解析画板：${item.name}`, PsdParseWarnType.Info);

                const rect = item.artboard?.rect || {
                    left: 0,
                    top: 0,
                    right: 0,
                    bottom: 0,
                };
                const width = rect.right - rect.left;
                const height = rect.bottom - rect.top;

                if ((width > 2448 || height > 2448) && !parseNoLimit) {
                    warnOrErrorCallback?.(`仅支持画板最长边小于 2448px，请重新上传`, PsdParseWarnType.Error);
                    return null;
                }

                const result = await parseLayers(
                    width,
                    height,
                    item.children || [],
                    fontList,
                    uploadFile,
                    noParseBgLayer,
                    item.name || '',
                    childrenCanvas[i],
                    (info: string, warnType: PsdParseWarnType, layerId?: string) => {
                        warnOrErrorCallback?.(`画板-${item.name}： ${info}`, warnType, layerId);
                    },
                    noReplaceFontFamily,
                    parseNoLimit,
                    parseLayerCallback,
                );

                result.name = item.name || '';

                if (psd.name.startsWith('ppt_')) {
                    result.settingType = getOldSettingType(result.name.split('_')[0]);
                }

                // 多画版 要减去画板本身的位置才是 图层在画板里面的位置
                traverseTrees(result.clientLayers, (layer: ILayerProps) => {
                    if (layer.layerType === LayerType.BACKGROUND_LAYER) {
                        return;
                    }
                    layer.left = layer.left - rect.left;
                    layer.top = layer.top - rect.top;
                });

                stages.push(result);
            }
        } else {
            const stage = await parseLayers(
                psd.width,
                psd.height,
                psd.children,
                fontList,
                uploadFile,
                noParseBgLayer,
                psd.name || '',
                psd.canvas,
                warnOrErrorCallback,
                noReplaceFontFamily,
                parseNoLimit,
                parseLayerCallback,
            );
            stage.name = psd.name || '';

            if (psd.name.startsWith('ppt_')) {
                stage.settingType = getOldSettingType(psd.name.split('_')[0]);
            }

            stages.push(stage);
        }

        return {
            psdChild: psd.children,
            name: psd.name,
            resolution,
            stages,
            psdFile,
        };
    } catch (error) {
        if (!window.navigator.onLine) {
            errorTypeRef.current = PsdParseErrorType.Network;
        }
        console.error('error ===>', {
            errorType: errorTypeRef.current,
            error,
            isOnline: window.navigator.onLine,
        });
        if (errorTypeRef.current === PsdParseErrorType.UploadApi) {
            warnOrErrorCallback?.(
                '上传服务异常，请稍后重试',
                PsdParseWarnType.Error,
                undefined,
                PsdParseErrorType.UploadApi,
            );
        } else if (errorTypeRef.current === PsdParseErrorType.Network) {
            warnOrErrorCallback?.(
                '网络异常，请检查网络连接',
                PsdParseWarnType.Error,
                undefined,
                PsdParseErrorType.Network,
            );
        } else {
            warnOrErrorCallback?.('模板解析失败', PsdParseWarnType.Error);
        }
        return null;
    }
};
